import {CampaignType} from 'common/src/modules/campaigns/campaigns.types';
import {ProductSelectionStrategyType} from 'common/src/modules/productSelectionStrategies/productSelectionStrategies.types';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import openAISystemMessage from 'common/src/modules/shared/copilot/openAI/utils/openAISystemMessage';
import openAIUserMessage from 'common/src/modules/shared/copilot/openAI/utils/openAIUserMessage';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import invokeStandaloneLLMCall from 'common/src/modules/shared/copilot/utils/invokeStandaloneLLMCall';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../campaignSetupAgent.enums';
import getStrategies, {FeedType} from './utils/getStrategies';

const inputSchema = z.object({
  feedType: z.nativeEnum(FeedType).describe('The feed type the user is working with'),
  prompt: z.string().min(1).describe("The user's natural language request to match"),
});

const responseSchema = z.object({
  strategyId: z.number(),
  name: z.string(),
});

function matchStrategyTool(store: CLStore) {
  async function execute({
    input: {feedType, prompt},
    abortSignal,
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    try {
      const campaign = store.getState().campaignPage.campaignTree.campaign;

      if (!campaign) {
        return toolResult('Campaign not found');
      }

      const campaignType = campaign.type;
      const systemPrompt = _buildSystemPrompt(feedType, campaignType);

      const messages = [
        openAISystemMessage(systemPrompt),
        openAIUserMessage(
          `User Prompt: ${prompt} \n
          Return the best matching strategy from the catalogue.`,
        ),
      ];

      const result = await invokeStandaloneLLMCall({
        messages,
        responseSchema,
        abortSignal,
      });

      return toolResult(JSON.stringify(result));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';

      return toolResult(`Error matching strategy: ${message}`);
    }
  }

  return tool({
    name: CampaignSetupAgentToolName.MatchStrategy,
    displayName: 'Getting the best strategy',
    description: 'Selects the best item selection strategy for Product, Location or Content feeds',
    execute,
    inputSchema,
  });
}

function _buildSystemPrompt(feedType: FeedType, campaignType: CampaignType) {
  const strategies = getStrategies(feedType, campaignType);

  const catalogue = strategies
    .map((s) => {
      return `ID: ${s.strategyId}
Name: ${s.name}
Description: ${s.description}`;
    })
    .join('\n\n');

  let specificFeedTypeRules = '### Domain specific rules: ### \n\n';

  if (feedType === FeedType.Product) {
    specificFeedTypeRules = `- When the user's request involves conditioning, prefer the ${ProductSelectionStrategyType.ProductMatching} strategy.
- When the user's request involves specific item selection, prefer the ${ProductSelectionStrategyType.SpecificProducts} strategy.
- When the user's request involves viewed items, prefer the ${ProductSelectionStrategyType.ViewedItems} strategy.`;
  }

  return `You are a precise matcher that selects the single best strategy for a user's request.
You need to analyze each and every strategy. Explain how well it fits the user's intent and choose the one that fits the best.

Strict rules:
- Never use startegies that are not in the strategies catalogue.
- If more than one strategy fits the user's intent, score each strategy and choose the one with the highest score.

${specificFeedTypeRules}

Strategies catalogue:
${catalogue}`;
}

export default matchStrategyTool;
